import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Database, Upload, Users, BellRing, Layers, Trash2, RefreshCw,
  CheckCircle2, AlertCircle, Send, Plus
} from 'lucide-react';
import { API_URL } from '../../config/api';
import logger from '../../utils/logger';

const apiCall = async (path, opts = {}) => {
  const token = localStorage.getItem('access_token');
  const res = await fetch(`${API_URL}${path}`, {
    credentials: 'include',
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(opts.headers || {}),
    },
  });
  const text = await res.text();
  if (!res.ok) throw new Error(text || `HTTP ${res.status}`);
  try { return JSON.parse(text); } catch (_) { return text; }
};

const Toast = ({ msg, kind, onClose }) => (
  <div
    className={`fixed top-4 right-4 z-50 px-4 py-2.5 rounded-lg shadow-lg text-sm font-medium flex items-center gap-2 ${
      kind === 'success' ? 'bg-emerald-500 text-white' : 'bg-rose-500 text-white'
    }`}
    onClick={onClose}
    data-testid="admin-tools-toast"
  >
    {kind === 'success' ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
    {msg}
  </div>
);

// =========================================================================
// 1. PROVIDERS & LABS
// =========================================================================
const ProvidersLabsTab = ({ toast }) => {
  const [stats, setStats] = useState({ doctors: '—', lab_tests: '—' });
  const [seeding, setSeeding] = useState(false);
  const [uploadingDoctors, setUploadingDoctors] = useState(false);
  const [uploadingLabs, setUploadingLabs] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const docs = await apiCall('/api/doctors?limit=1');
      const labs = await apiCall('/api/healthtrack/lab-tests/catalog');
      setStats({
        doctors: docs?.total ?? docs?.count ?? (Array.isArray(docs?.doctors) ? docs.doctors.length : '—'),
        lab_tests: labs?.count ?? (Array.isArray(labs?.tests) ? labs.tests.length : '—'),
      });
    } catch (e) {
      logger.warn('Stat refresh failed', e);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const seed = async (force = false) => {
    if (!window.confirm(force ? 'Re-seed will WIPE existing seed_v1 docs. Continue?' : 'Seed initial 96 doctors + 70 lab tests?')) return;
    try {
      setSeeding(true);
      const res = await apiCall(`/api/admin/seed/doctors-and-labs?force=${force}`, { method: 'POST' });
      toast(res.skipped
        ? `Already seeded — ${res.doctors} doctors, ${res.lab_tests} tests`
        : `✓ Inserted ${res.doctors_inserted} doctors + ${res.lab_tests_inserted} tests`,
        'success');
      refresh();
    } catch (e) {
      toast('Seed failed: ' + e.message, 'error');
    } finally {
      setSeeding(false);
    }
  };

  const uploadCsv = async (kind, file) => {
    const setter = kind === 'doctors' ? setUploadingDoctors : setUploadingLabs;
    setter(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const token = localStorage.getItem('access_token');
      const res = await fetch(
        `${API_URL}/api/admin/import/${kind === 'doctors' ? 'doctors' : 'lab-tests'}`,
        {
          method: 'POST',
          credentials: 'include',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          body: fd,
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Import failed');
      toast(`✓ Imported ${data.inserted} rows${data.errors?.length ? ` · ${data.errors.length} errors` : ''}`, 'success');
      refresh();
    } catch (e) {
      toast('Import failed: ' + e.message, 'error');
    } finally {
      setter(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-3">
          <div className="text-xs text-orange-700 font-semibold uppercase">Doctors</div>
          <div className="text-2xl font-bold text-orange-900" data-testid="stat-doctors">{stats.doctors}</div>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3">
          <div className="text-xs text-emerald-700 font-semibold uppercase">Lab Tests</div>
          <div className="text-2xl font-bold text-emerald-900" data-testid="stat-labs">{stats.lab_tests}</div>
        </div>
      </div>

      <Card>
        <CardHeader className="py-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Database className="w-4 h-4 text-orange-600" /> One-click seed
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-600 mb-3">
            Pre-load the directory with 96 doctors (allopathic + Ayurvedic) across 8 Indian cities and 70 standard lab tests. Idempotent — safe to click twice.
          </p>
          <div className="flex gap-2 flex-wrap">
            <Button onClick={() => seed(false)} disabled={seeding} data-testid="seed-button">
              {seeding ? <RefreshCw className="w-4 h-4 mr-1.5 animate-spin" /> : <Plus className="w-4 h-4 mr-1.5" />}
              Seed providers
            </Button>
            <Button variant="outline" onClick={() => seed(true)} disabled={seeding} data-testid="seed-force-button">
              <RefreshCw className="w-4 h-4 mr-1.5" /> Re-seed (force)
            </Button>
            <Button variant="ghost" onClick={refresh} data-testid="refresh-stats">
              <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh stats
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="py-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Upload className="w-4 h-4 text-orange-600" /> Bulk CSV import
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-sm">Doctors CSV</Label>
            <p className="text-xs text-gray-500 mb-1.5">
              Columns: name, type, specialty, qualification, experience_years, city, state, consultation_fee, rating, languages.
              <a className="text-orange-600 ml-2 underline"
                 href={`${API_URL}/api/admin/import/doctors/template.csv`}
                 target="_blank" rel="noreferrer"
                 data-testid="dl-doctor-template">Download template</a>
            </p>
            <input type="file" accept=".csv"
                   data-testid="upload-doctors-csv"
                   disabled={uploadingDoctors}
                   onChange={(e) => e.target.files?.[0] && uploadCsv('doctors', e.target.files[0])}
                   className="text-sm" />
          </div>
          <div>
            <Label className="text-sm">Lab tests CSV</Label>
            <p className="text-xs text-gray-500 mb-1.5">
              Columns: test_name, category, fasting_required, price, tat_hours.
              <a className="text-orange-600 ml-2 underline"
                 href={`${API_URL}/api/admin/import/lab-tests/template.csv`}
                 target="_blank" rel="noreferrer"
                 data-testid="dl-lab-template">Download template</a>
            </p>
            <input type="file" accept=".csv"
                   data-testid="upload-labs-csv"
                   disabled={uploadingLabs}
                   onChange={(e) => e.target.files?.[0] && uploadCsv('lab-tests', e.target.files[0])}
                   className="text-sm" />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// =========================================================================
// 2. PLANS
// =========================================================================
const PlansTab = ({ toast }) => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const blank = { code: '', name: '', target: 'patient', price_inr_monthly: 199,
                  price_inr_yearly: 1990, features: '', is_active: true, sort_order: 0 };
  const [form, setForm] = useState(blank);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiCall('/api/admin/plans');
      setPlans(r.plans || []);
    } catch (e) {
      toast('Load plans failed: ' + e.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { refresh(); }, [refresh]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const body = {
        ...form,
        price_inr_monthly: Number(form.price_inr_monthly),
        price_inr_yearly: form.price_inr_yearly ? Number(form.price_inr_yearly) : null,
        sort_order: Number(form.sort_order || 0),
        features: typeof form.features === 'string'
          ? form.features.split('\n').map(s => s.trim()).filter(Boolean)
          : form.features,
      };
      await apiCall('/api/admin/plans', { method: 'POST', body: JSON.stringify(body) });
      toast(`✓ Saved plan "${body.name}"`, 'success');
      setForm(blank);
      refresh();
    } catch (e) {
      toast('Save failed: ' + e.message, 'error');
    }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Deactivate plan "${name}"?`)) return;
    try {
      await apiCall(`/api/admin/plans/${id}`, { method: 'DELETE' });
      toast('✓ Plan deactivated', 'success');
      refresh();
    } catch (e) {
      toast('Delete failed: ' + e.message, 'error');
    }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <Card>
        <CardHeader className="py-3">
          <CardTitle className="text-base">Add / update plan</CardTitle>
        </CardHeader>
        <CardContent>
          <form className="space-y-2.5 text-sm" onSubmit={submit} data-testid="plan-form">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Code</Label>
                <Input value={form.code} required onChange={e => setForm({...form, code: e.target.value})}
                       placeholder="patient_premium" data-testid="plan-code" />
              </div>
              <div>
                <Label>Display name</Label>
                <Input value={form.name} required onChange={e => setForm({...form, name: e.target.value})}
                       placeholder="Premium Patient" data-testid="plan-name" />
              </div>
              <div>
                <Label>Target</Label>
                <select className="w-full border rounded h-9 px-2 text-sm"
                        value={form.target}
                        onChange={e => setForm({...form, target: e.target.value})}
                        data-testid="plan-target">
                  {['patient','clinic','doctor','hospital','lab'].map(t =>
                    <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <Label>Sort order</Label>
                <Input type="number" value={form.sort_order}
                       onChange={e => setForm({...form, sort_order: e.target.value})}
                       data-testid="plan-sort" />
              </div>
              <div>
                <Label>Monthly (₹)</Label>
                <Input type="number" value={form.price_inr_monthly} required
                       onChange={e => setForm({...form, price_inr_monthly: e.target.value})}
                       data-testid="plan-monthly" />
              </div>
              <div>
                <Label>Yearly (₹)</Label>
                <Input type="number" value={form.price_inr_yearly}
                       onChange={e => setForm({...form, price_inr_yearly: e.target.value})}
                       data-testid="plan-yearly" />
              </div>
            </div>
            <div>
              <Label>Features (one per line)</Label>
              <textarea value={form.features}
                        onChange={e => setForm({...form, features: e.target.value})}
                        className="w-full border rounded p-2 text-sm" rows={3}
                        placeholder="Unlimited AI Health Reports&#10;Priority tele-consults&#10;Family plan up to 4"
                        data-testid="plan-features" />
            </div>
            <Button type="submit" data-testid="plan-save">
              <Plus className="w-4 h-4 mr-1.5" /> Save plan
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="py-3">
          <CardTitle className="text-base flex items-center justify-between">
            Existing plans
            <Button variant="ghost" size="sm" onClick={refresh}>
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {plans.length === 0 ? (
            <p className="text-sm text-gray-500">No plans yet. Add one on the left.</p>
          ) : (
            <div className="space-y-2">
              {plans.map(p => (
                <div key={p.id}
                     className={`border rounded-lg p-2.5 ${p.is_active ? 'bg-white' : 'bg-gray-50 opacity-60'}`}
                     data-testid={`plan-row-${p.code}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-semibold text-sm">{p.name} <span className="text-xs text-gray-400">({p.code})</span></div>
                      <div className="text-xs text-gray-600">
                        Target: <b>{p.target}</b> · ₹{p.price_inr_monthly}/mo
                        {p.price_inr_yearly ? ` · ₹${p.price_inr_yearly}/yr` : ''}
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => remove(p.id, p.name)}
                            data-testid={`plan-delete-${p.code}`}>
                      <Trash2 className="w-4 h-4 text-rose-500" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// =========================================================================
// 3. BULK USER ACTIONS
// =========================================================================
const BulkUserActionsTab = ({ toast }) => {
  const [userIds, setUserIds] = useState('');
  const [action, setAction] = useState('deactivate');
  const [newRole, setNewRole] = useState('user');
  const [running, setRunning] = useState(false);

  const submit = async () => {
    const ids = userIds.split(/[\s,]+/).map(s => s.trim()).filter(Boolean);
    if (ids.length === 0) return toast('Paste at least one user_id', 'error');
    if (!window.confirm(`${action} ${ids.length} user(s)?`)) return;
    setRunning(true);
    try {
      const body = { user_ids: ids, action };
      if (action === 'set_role') body.new_role = newRole;
      const r = await apiCall('/api/admin/users/bulk-action', {
        method: 'POST', body: JSON.stringify(body)
      });
      toast(`✓ ${r.action} affected ${r.affected}/${r.requested} users`, 'success');
    } catch (e) {
      toast('Bulk action failed: ' + e.message, 'error');
    } finally {
      setRunning(false);
    }
  };

  return (
    <Card>
      <CardHeader className="py-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Users className="w-4 h-4 text-orange-600" /> Bulk user actions
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <div>
          <Label>User IDs</Label>
          <textarea value={userIds} onChange={e => setUserIds(e.target.value)}
                    className="w-full border rounded p-2 text-xs font-mono"
                    placeholder="paste user IDs separated by commas, spaces or newlines"
                    rows={4} data-testid="bulk-user-ids" />
          <p className="text-xs text-gray-500 mt-1">{userIds.split(/[\s,]+/).filter(Boolean).length} ID(s) entered.</p>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label>Action</Label>
            <select value={action} onChange={e => setAction(e.target.value)}
                    className="w-full border rounded h-9 px-2"
                    data-testid="bulk-action-select">
              <option value="activate">Activate</option>
              <option value="deactivate">Deactivate</option>
              <option value="set_role">Change role</option>
              <option value="delete">Soft delete</option>
            </select>
          </div>
          {action === 'set_role' && (
            <div>
              <Label>New role</Label>
              <select value={newRole} onChange={e => setNewRole(e.target.value)}
                      className="w-full border rounded h-9 px-2"
                      data-testid="bulk-new-role">
                {['user','doctor','clinic','lab','admin'].map(r =>
                  <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          )}
        </div>
        <Button onClick={submit} disabled={running} data-testid="bulk-submit"
                className="bg-orange-600 hover:bg-orange-700">
          {running ? <RefreshCw className="w-4 h-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1.5" />}
          Run bulk action
        </Button>
      </CardContent>
    </Card>
  );
};

// =========================================================================
// 4. NOTIFICATIONS
// =========================================================================
const NotificationsTab = ({ toast }) => {
  const [segments, setSegments] = useState([]);
  const [history, setHistory] = useState([]);
  const [segName, setSegName] = useState('');
  const [segDesc, setSegDesc] = useState('');
  const [segFilter, setSegFilter] = useState('{"role":"user"}');
  const [composing, setComposing] = useState({
    segment_id: '', title: '', message: '', channels: ['in_app'], schedule_at: '',
  });
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const s = await apiCall('/api/admin/notifications/segments');
      setSegments(s.segments || []);
      const h = await apiCall('/api/admin/notifications/history?limit=20');
      setHistory(h.notifications || []);
    } catch (e) {
      logger.warn('Notif load failed', e);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const createSegment = async () => {
    if (!segName.trim()) return toast('Segment name required', 'error');
    let filter;
    try { filter = JSON.parse(segFilter); }
    catch (e) { return toast('Filter must be valid JSON', 'error'); }
    try {
      await apiCall('/api/admin/notifications/segments', {
        method: 'POST',
        body: JSON.stringify({ name: segName, description: segDesc, filter, is_active: true }),
      });
      toast(`✓ Segment "${segName}" created`, 'success');
      setSegName(''); setSegDesc(''); setSegFilter('{"role":"user"}');
      refresh();
    } catch (e) {
      toast('Create segment failed: ' + e.message, 'error');
    }
  };

  const deleteSegment = async (id, name) => {
    if (!window.confirm(`Delete segment "${name}"?`)) return;
    try {
      await apiCall(`/api/admin/notifications/segments/${id}`, { method: 'DELETE' });
      refresh();
    } catch (e) {
      toast('Delete failed: ' + e.message, 'error');
    }
  };

  const sendNotification = async () => {
    if (!composing.title.trim() || !composing.message.trim())
      return toast('Title and message required', 'error');
    if (!composing.segment_id)
      return toast('Pick a segment', 'error');
    setBusy(true);
    try {
      const r = await apiCall('/api/admin/notifications/send', {
        method: 'POST',
        body: JSON.stringify({
          segment_id: composing.segment_id,
          title: composing.title,
          message: composing.message,
          channels: composing.channels,
          schedule_at: composing.schedule_at || null,
        }),
      });
      toast(
        `✓ ${r.notification.status === 'sent'
          ? `Sent to ${r.notification.audience_count} users (${r.notification.delivered_in_app} in-app delivered)`
          : 'Scheduled'}`,
        'success'
      );
      setComposing({ segment_id: '', title: '', message: '', channels: ['in_app'], schedule_at: '' });
      refresh();
    } catch (e) {
      toast('Send failed: ' + e.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const toggleChannel = (c) => setComposing(p => ({
    ...p,
    channels: p.channels.includes(c) ? p.channels.filter(x => x !== c) : [...p.channels, c],
  }));

  return (
    <div className="grid lg:grid-cols-2 gap-4">
      {/* Segments */}
      <Card>
        <CardHeader className="py-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Layers className="w-4 h-4 text-orange-600" /> Segments
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2.5 text-sm">
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="Segment name (e.g. Mumbai patients)"
                   value={segName} onChange={e => setSegName(e.target.value)}
                   data-testid="seg-name" />
            <Input placeholder="Description (optional)"
                   value={segDesc} onChange={e => setSegDesc(e.target.value)} />
          </div>
          <Label className="text-xs">Mongo-style filter (applied to users collection)</Label>
          <textarea value={segFilter} onChange={e => setSegFilter(e.target.value)}
                    className="w-full border rounded p-2 text-xs font-mono"
                    rows={3} data-testid="seg-filter"
                    placeholder='{"role":"user","city":"Mumbai"}' />
          <Button size="sm" onClick={createSegment} data-testid="seg-create">
            <Plus className="w-4 h-4 mr-1" /> Create segment
          </Button>

          <div className="pt-2 space-y-1.5">
            {segments.length === 0 ? (
              <p className="text-xs text-gray-500">No segments yet.</p>
            ) : segments.map(s => (
              <div key={s.id} className="border rounded p-2 flex items-center justify-between"
                   data-testid={`seg-row-${s.id}`}>
                <div>
                  <div className="font-medium text-sm">{s.name}</div>
                  <div className="text-[11px] text-gray-500">
                    {s.audience_count} users · {JSON.stringify(s.filter).slice(0, 60)}
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => deleteSegment(s.id, s.name)}>
                  <Trash2 className="w-4 h-4 text-rose-500" />
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Compose */}
      <Card>
        <CardHeader className="py-3">
          <CardTitle className="text-base flex items-center gap-2">
            <BellRing className="w-4 h-4 text-orange-600" /> Compose
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2.5 text-sm">
          <div>
            <Label>Segment</Label>
            <select className="w-full border rounded h-9 px-2"
                    value={composing.segment_id}
                    onChange={e => setComposing({...composing, segment_id: e.target.value})}
                    data-testid="compose-segment">
              <option value="">— pick a segment —</option>
              {segments.map(s => <option key={s.id} value={s.id}>{s.name} ({s.audience_count})</option>)}
            </select>
          </div>
          <Input placeholder="Title" value={composing.title}
                 onChange={e => setComposing({...composing, title: e.target.value})}
                 data-testid="compose-title" />
          <textarea placeholder="Message" value={composing.message}
                    onChange={e => setComposing({...composing, message: e.target.value})}
                    className="w-full border rounded p-2 text-sm" rows={3}
                    data-testid="compose-message" />
          <div className="flex gap-3">
            {['in_app','email','sms','push'].map(c => (
              <label key={c} className="text-xs flex items-center gap-1">
                <input type="checkbox"
                       checked={composing.channels.includes(c)}
                       onChange={() => toggleChannel(c)}
                       data-testid={`compose-channel-${c}`} />
                {c}
              </label>
            ))}
          </div>
          <div>
            <Label className="text-xs">Schedule at (optional)</Label>
            <Input type="datetime-local"
                   value={composing.schedule_at}
                   onChange={e => setComposing({...composing, schedule_at: e.target.value})} />
          </div>
          <Button onClick={sendNotification} disabled={busy} data-testid="compose-send">
            {busy ? <RefreshCw className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}
            {composing.schedule_at ? 'Schedule' : 'Send now'}
          </Button>
        </CardContent>
      </Card>

      {/* History */}
      <Card className="lg:col-span-2">
        <CardHeader className="py-3">
          <CardTitle className="text-base">History</CardTitle>
        </CardHeader>
        <CardContent>
          {history.length === 0 ? (
            <p className="text-sm text-gray-500">No notifications sent yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-left bg-gray-50">
                  <tr>
                    <th className="p-2">Title</th>
                    <th className="p-2">Segment</th>
                    <th className="p-2">Audience</th>
                    <th className="p-2">Channels</th>
                    <th className="p-2">Status</th>
                    <th className="p-2">Sent</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map(h => (
                    <tr key={h.id} className="border-t" data-testid={`history-row-${h.id}`}>
                      <td className="p-2 font-medium">{h.title}</td>
                      <td className="p-2">{h.segment_name || '—'}</td>
                      <td className="p-2">{h.audience_count}</td>
                      <td className="p-2">{(h.channels || []).join(', ')}</td>
                      <td className="p-2">{h.status}</td>
                      <td className="p-2">{h.created_at?.slice(0, 16).replace('T', ' ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// =========================================================================
// MAIN
// =========================================================================
const AdminTools = () => {
  const [toastState, setToastState] = useState(null);
  const showToast = (msg, kind = 'success') => {
    setToastState({ msg, kind });
    setTimeout(() => setToastState(null), 4000);
  };

  return (
    <div className="max-w-6xl mx-auto p-4" data-testid="admin-tools-page">
      <h1 className="text-xl font-bold mb-3 flex items-center gap-2">
        <Database className="w-5 h-5 text-orange-600" /> Admin Tools
      </h1>
      <p className="text-sm text-gray-500 mb-4">
        Seed the catalogue, manage subscription plans, run bulk user actions, and broadcast notifications.
      </p>

      <Tabs defaultValue="providers">
        <TabsList className="mb-3 flex-wrap">
          <TabsTrigger value="providers" data-testid="tab-providers">Providers & Labs</TabsTrigger>
          <TabsTrigger value="plans" data-testid="tab-plans">Plans</TabsTrigger>
          <TabsTrigger value="bulk" data-testid="tab-bulk">Bulk users</TabsTrigger>
          <TabsTrigger value="notifications" data-testid="tab-notifications">Notifications</TabsTrigger>
        </TabsList>
        <TabsContent value="providers"><ProvidersLabsTab toast={showToast} /></TabsContent>
        <TabsContent value="plans"><PlansTab toast={showToast} /></TabsContent>
        <TabsContent value="bulk"><BulkUserActionsTab toast={showToast} /></TabsContent>
        <TabsContent value="notifications"><NotificationsTab toast={showToast} /></TabsContent>
      </Tabs>

      {toastState && <Toast {...toastState} onClose={() => setToastState(null)} />}
    </div>
  );
};

export default AdminTools;
